/**
 * Per-page opt-in for the collapsing-header scroll behavior.
 *
 * The keys mirror the `ActiveLayoutView` union declared in `src/App.tsx`.
 * When `collapsingHeader` is true, the page's shell wraps in
 * `<PageScrollShell>` and gets the "Row 2 scrolls away, Row 3 docks under the
 * top bar, then inner content scrolls" behavior described in the unification
 * plan.
 *
 * Chat- and composer-style surfaces (AI Magic, Chat) opt out because their
 * header is a persistent toolbar that must stay visible while the user types.
 * Immersive / short surfaces (Focus, Raid) opt out because they have nothing
 * long enough to justify the interaction.
 */

export type PageScrollBehaviorKey =
  | 'DASHBOARD'
  | 'CALENDAR'
  | 'EXPLORE'
  | 'USER_SETTINGS'
  | 'YOUR_PAGE'
  | 'PURCHASE'
  | 'INVITES'
  | 'LINK'
  | 'STOCK'
  | 'BUDGET'
  | 'QUOTA'
  | 'TEACHING'
  | 'TRAINING'
  | 'UPLOAD'
  | 'FORMS'
  | 'MEETINGS'
  | 'AUTOMATE'
  | 'DESIGN'
  | 'AI_MAGIC'
  | 'CHAT'
  | 'CHECKLISTS'
  | 'MANAGE'
  | 'RAID'
  | 'FOR_YOU'
  | 'FOCUS'
  | 'WORKSPACE_SETTINGS'
  | 'SUMMARY'
  | 'OVERVIEW'
  | 'REPORTS'

export type PageScrollBehavior = {
  /** When true, the page participates in the collapsing-header scroll dance. */
  collapsingHeader: boolean
}

export const PAGE_SCROLL_BEHAVIOR: Record<PageScrollBehaviorKey, PageScrollBehavior> = {
  // Long / scrollable hubs — participate.
  DESIGN:             { collapsingHeader: true },
  DASHBOARD:          { collapsingHeader: true },
  OVERVIEW:           { collapsingHeader: true },
  CALENDAR:           { collapsingHeader: true },
  MEETINGS:           { collapsingHeader: true },
  CHECKLISTS:         { collapsingHeader: true },
  FORMS:              { collapsingHeader: true },
  AUTOMATE:           { collapsingHeader: true },
  UPLOAD:             { collapsingHeader: true },
  INVITES:            { collapsingHeader: true },
  SUMMARY:            { collapsingHeader: true },
  REPORTS:            { collapsingHeader: true },
  LINK:               { collapsingHeader: true },
  STOCK:              { collapsingHeader: true },
  BUDGET:             { collapsingHeader: true },
  QUOTA:              { collapsingHeader: true },
  TEACHING:           { collapsingHeader: true },
  TRAINING:           { collapsingHeader: true },
  MANAGE:             { collapsingHeader: true },
  FOR_YOU:            { collapsingHeader: true },
  EXPLORE:            { collapsingHeader: true },
  WORKSPACE_SETTINGS: { collapsingHeader: true },
  USER_SETTINGS:      { collapsingHeader: true },
  YOUR_PAGE:          { collapsingHeader: true },
  PURCHASE:           { collapsingHeader: true },

  // Chat / composer surfaces — the header is a live toolbar; don't collapse.
  AI_MAGIC:           { collapsingHeader: false },
  CHAT:               { collapsingHeader: false },

  // Immersive / short surfaces — nothing long enough to warrant collapsing.
  FOCUS:              { collapsingHeader: false },
  RAID:               { collapsingHeader: false },
}

export const isCollapsingHeaderEligible = (key: PageScrollBehaviorKey): boolean =>
  PAGE_SCROLL_BEHAVIOR[key]?.collapsingHeader ?? false
